import path from "path";
import fs from "fs";
import { findPackageJson, safeReadJson } from "../utils/files.js";
import { analyzeDependencies, isDependencyNative } from "../analyze/dependencyAnalyzer.js";
import { loadPluginsForProject } from "../plugins/loader.js";
import { saveSnapshot, loadSnapshotIndex, loadSnapshot } from "./snapshotStore.js";
import type { ProjectSnapshot, SnapshotDependency } from "../models/Snapshot.js";

const LOCKFILES = ["package-lock.json", "yarn.lock", "pnpm-lock.yaml", "bun.lock"];

function generateSnapshotId(): string {
  // ISO timestamp without colons/dots — deterministic across same second
  return new Date().toISOString().replace(/[:.]/g, "-").replace("T", "_").replace("Z", "");
}

function detectLockfile(projectRoot: string): ProjectSnapshot["lockfileMeta"] {
  for (const lf of LOCKFILES) {
    const lfPath = path.join(projectRoot, lf);
    if (fs.existsSync(lfPath)) {
      const stat = fs.statSync(lfPath);
      return { name: lf, sizeBytes: stat.size };
    }
  }
  return null;
}

/**
 * Creates a new project snapshot capturing the current state of package.json,
 * dependencies, loaded plugins, and lockfile metadata.
 */
export async function createSnapshot(
  cwd: string,
  label?: string
): Promise<ProjectSnapshot> {
  const packageJsonPath = findPackageJson(cwd);
  if (!packageJsonPath) throw new Error("package.json not found.");
  const pkg = safeReadJson(packageJsonPath);
  if (!pkg) throw new Error("package.json is corrupted.");

  const projectRoot = path.dirname(packageJsonPath);
  const depAnalysis = analyzeDependencies(projectRoot, pkg as Record<string, unknown>);
  const loadedPlugins = await loadPluginsForProject(projectRoot);

  const allDepsEntries = Object.entries({
    ...(pkg["dependencies"] as Record<string, string> || {}),
    ...(pkg["devDependencies"] as Record<string, string> || {}),
  });

  const dependencies: SnapshotDependency[] = allDepsEntries.map(([name, version]) => ({
    name,
    version: version.replace(/^[^0-9]*/, "") || version,
    isNative: isDependencyNative(projectRoot, name),
  }));

  const expoVersionSpec = depAnalysis.allDeps["expo"] || "";
  const expoVersion = expoVersionSpec ? expoVersionSpec.replace(/^[^0-9]*/, "") : null;

  const id = generateSnapshotId();
  const createdAt = new Date().toISOString();
  const snapshotLabel = label || `Snapshot ${createdAt.slice(0, 10)}`;

  const snapshot: ProjectSnapshot = {
    id,
    label: snapshotLabel,
    createdAt,
    reactNativeVersion: depAnalysis.reactNativeVersion,
    expoVersion: expoVersion || null,
    loadedPlugins: loadedPlugins.map((p) => p.packageName),
    dependencies,
    packageJsonSnapshot: JSON.stringify(pkg),
    lockfileMeta: detectLockfile(projectRoot),
  };

  saveSnapshot(projectRoot, snapshot);
  return snapshot;
}

/**
 * Lists all snapshots for the current project (summary only).
 */
export function listSnapshots(cwd: string): { id: string; label: string; createdAt: string }[] {
  const packageJsonPath = findPackageJson(cwd);
  if (!packageJsonPath) return [];
  const projectRoot = path.dirname(packageJsonPath);
  return loadSnapshotIndex(projectRoot);
}

/**
 * Restores a snapshot by ID.
 *
 * "Restore" in this context means:
 *   - Printing the package.json content that was captured
 *   - Listing the dependencies that should be reinstalled
 *
 * Actual file writes are optional and must be confirmed by the caller.
 */
export function restoreSnapshot(
  cwd: string,
  id: string
): { snapshot: ProjectSnapshot; packageJsonContent: string } | null {
  const packageJsonPath = findPackageJson(cwd);
  if (!packageJsonPath) return null;
  const projectRoot = path.dirname(packageJsonPath);
  const snapshot = loadSnapshot(projectRoot, id);
  if (!snapshot) return null;

  return {
    snapshot,
    packageJsonContent: snapshot.packageJsonSnapshot,
  };
}
